import { useEffect, useRef, useState } from 'react'
import type { TrainerDto, TrainerRole } from '@/api/admin'
import { ChevronDown, Search, X, AlertTriangle } from 'lucide-react'

export type TrainerAssignment = { trainerId: string; role: TrainerRole }
export type TrainerConflictMap = Record<string, string[]>

function trainerName(t: TrainerDto) {
  return `${t.firstName} ${t.lastName}`
}

function useClickOutside(onOutside: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onOutside])
  return ref
}

// ─── Single-select: lead trainer, default "To be confirmed" ───────────────────

function TrainerSingleSelect({
  trainers, value, onChange, conflictMap, placeholder = 'To be confirmed',
}: {
  trainers: TrainerDto[]
  value: string | null
  onChange: (id: string | null) => void
  conflictMap?: TrainerConflictMap
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useClickOutside(() => { setOpen(false); setSearch('') })

  const selected = trainers.find(t => t.id === value) ?? null
  const filtered = trainers.filter(t =>
    trainerName(t).toLowerCase().includes(search.toLowerCase().trim())
  )

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between h-10 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? trainerName(selected) : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {selected && conflictMap?.[selected.id] && (
            <span title={`Double-booked: ${conflictMap[selected.id].join('; ')}`}>
              <AlertTriangle size={13} className="text-amber-500" />
            </span>
          )}
          {selected && (
            <span
              onClick={e => { e.stopPropagation(); onChange(null) }}
              className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
            >
              <X size={13} />
            </span>
          )}
          <ChevronDown size={14} className="text-gray-400" />
        </div>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="relative border-b border-gray-100">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search trainers…"
              className="w-full pl-8 pr-2 py-2 text-sm focus:outline-none"
            />
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false); setSearch('') }}
              className={`w-full text-left px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 ${!value ? 'text-indigo-600 font-medium' : 'text-gray-400'}`}
            >
              To be confirmed
            </button>
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400">No trainers found.</p>
            ) : filtered.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => { onChange(t.id); setOpen(false); setSearch('') }}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 ${t.id === value ? 'text-indigo-600 font-medium bg-indigo-50/50' : 'text-gray-800'}`}
              >
                {trainerName(t)}
                {conflictMap?.[t.id] && <AlertTriangle size={12} className="text-amber-500 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Multi-select: assistant trainers ──────────────────────────────────────────

function TrainerMultiSelect({
  trainers, values, onChange, conflictMap,
}: {
  trainers: TrainerDto[]
  values: string[]
  onChange: (ids: string[]) => void
  conflictMap?: TrainerConflictMap
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useClickOutside(() => { setOpen(false); setSearch('') })

  const selected = values.map(id => trainers.find(t => t.id === id)).filter((t): t is TrainerDto => !!t)
  const available = trainers.filter(t => !values.includes(t.id))
  const filtered = available.filter(t =>
    trainerName(t).toLowerCase().includes(search.toLowerCase().trim())
  )

  const add = (id: string) => { onChange([...values, id]); setSearch('') }
  const remove = (id: string) => onChange(values.filter(v => v !== id))

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setOpen(true)}
        className="w-full min-h-10 flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm shadow-sm cursor-text focus-within:ring-1 focus-within:ring-ring"
      >
        {selected.map(t => (
          <span key={t.id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 pl-2.5 pr-1.5 py-0.5 text-xs">
            {trainerName(t)}
            {conflictMap?.[t.id] && <AlertTriangle size={11} className="text-amber-500" />}
            <span
              onClick={e => { e.stopPropagation(); remove(t.id) }}
              className="hover:text-indigo-900 cursor-pointer p-0.5"
            >
              <X size={11} />
            </span>
          </span>
        ))}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? 'None assigned' : ''}
          className="flex-1 min-w-20 py-0.5 text-sm focus:outline-none placeholder:text-gray-400"
        />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="max-h-48 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400">
                {available.length === 0 ? 'No more trainers to add.' : 'No trainers found.'}
              </p>
            ) : filtered.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => add(t.id)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-sm text-gray-800 cursor-pointer hover:bg-gray-50"
              >
                {trainerName(t)}
                {conflictMap?.[t.id] && <AlertTriangle size={12} className="text-amber-500 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Combined lead + assistants fields ─────────────────────────────────────────

export function TrainerAssignmentFields({
  trainers, assignments, onChange, conflictMap,
}: {
  trainers: TrainerDto[]
  assignments: TrainerAssignment[]
  onChange: (next: TrainerAssignment[]) => void
  conflictMap?: TrainerConflictMap
}) {
  const leadId = assignments.find(a => a.role === 'Lead')?.trainerId ?? null
  const assistantIds = assignments.filter(a => a.role === 'Assistant').map(a => a.trainerId)

  const setLead = (id: string | null) => {
    const assistants = assignments.filter(a => a.role === 'Assistant' && a.trainerId !== id)
    onChange(id ? [{ trainerId: id, role: 'Lead' as TrainerRole }, ...assistants] : assistants)
  }
  const setAssistants = (ids: string[]) => {
    const lead = assignments.find(a => a.role === 'Lead')
    onChange([...(lead ? [lead] : []), ...ids.map(trainerId => ({ trainerId, role: 'Assistant' as TrainerRole }))])
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">Lead trainer</label>
        <TrainerSingleSelect
          trainers={trainers}
          value={leadId}
          onChange={setLead}
          conflictMap={conflictMap}
        />
      </div>
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">Assistant trainers</label>
        <TrainerMultiSelect
          trainers={trainers.filter(t => t.id !== leadId)}
          values={assistantIds}
          onChange={setAssistants}
          conflictMap={conflictMap}
        />
      </div>
    </div>
  )
}
